import { INCLUDED_CREDITS_KEY, INCLUDED_CREDITS_PER_SEAT_KEY } from "@/components/pricing/plan-catalog";
import type { V2Tile } from "./v2-tiles";

/** The catalog's "unlimited" sentinel (see `LimitsService`). */
const UNLIMITED = -1;

/** A `V2Theme` translator: keys are relative to the namespace (`limits.messagesPerDay`). */
export type V2ThemeTranslator = (key: string, values?: Record<string, string | number>) => string;

const MESSAGE_LIMITS = [
  { key: "chat.messages_per_day", label: "limits.messagesPerDay" },
  { key: "chat.messages_per_hour", label: "limits.messagesPerHour" },
] as const;

/**
 * A plan's headline limits as V2 tiles: messages a day, messages an hour and
 * monthly credits, in that order. Values are read from `plan.limits` (never
 * typed by hand) and formatted for `locale`; `-1` reads "Unlimited".
 *
 * Skipped: a key the plan does not carry, and a `0` (the plan does not
 * include it — "0 messages an hour" says less than nothing). Credits use the
 * per-seat rate when the plan has one (org plans), else the flat allowance.
 */
export function planLimitTiles(
  limits: Readonly<Record<string, number>> | null | undefined,
  t: V2ThemeTranslator,
  locale: string = "en-US",
): V2Tile[] {
  if (!limits) return [];
  const number = new Intl.NumberFormat(locale);
  const format = (value: number) => (value === UNLIMITED ? t("limit.unlimited") : number.format(value));
  const tiles: V2Tile[] = [];

  for (const spec of MESSAGE_LIMITS) {
    const value = limits[spec.key];
    if (typeof value !== "number" || value === 0) continue;
    tiles.push({ key: spec.key, value: format(value), label: t(spec.label) });
  }

  const perSeat = limits[INCLUDED_CREDITS_PER_SEAT_KEY];
  const flat = limits[INCLUDED_CREDITS_KEY];
  if (typeof perSeat === "number" && perSeat !== 0) {
    tiles.push({ key: INCLUDED_CREDITS_PER_SEAT_KEY, value: format(perSeat), label: t("limits.creditsPerSeatMonth") });
  } else if (typeof flat === "number" && flat !== 0) {
    tiles.push({ key: INCLUDED_CREDITS_KEY, value: format(flat), label: t("limits.creditsPerMonth") });
  }

  return tiles;
}
